import React, { useState, useEffect, useLayoutEffect, useRef, useMemo } from "react";
import CodeEditor from "@uiw/react-textarea-code-editor";
import LandingPage from "./components/LandingPage";
import SecurityWarning from "./components/SecurityWarning";
import LanguageToggle from "./components/LanguageToggle";
import NetworkConfirmModal from "./components/NetworkConfirmModal";
import NetworkLogPanel from "./components/NetworkLogPanel";
import WhitelistSettings from "./components/WhitelistSettings";
import AiEditPanel from "./components/AiEditPanel";
import AuthControl from "./components/AuthControl";
import AdminPanel from "./components/AdminPanel";
import { useLanguage } from "./contexts/LanguageContext";
import { useAuth } from "./contexts/AuthContext";
import SEO from "./components/SEO";
import { buildNetworkSecurityScript, buildCspMetaTag, buildPreviewFindScript, buildSelectionLinkScript, buildScrollSyncScript } from "./lib/networkSecurityScript";
import { injectSourceLineAttrs } from "./lib/sourceLineMap";
import FindBar from "./components/FindBar";
import { DEVICE_PRESETS, getDevicePreset } from "./lib/devicePresets";
import { buildStandaloneHtml, ensureCharset } from "./lib/paperSkills";
import { lintPrintIssues, fixPrintIssues, lintPrintOverflow, fixPrintOverflow } from "./lib/printLint";
import { buildMarkdownDocument } from "./lib/markdownPreview";
import { 
  FiSun, 
  FiMoon, 
  FiMaximize, 
  FiMinimize, 
  FiPlay, 
  FiX,
  FiTerminal,
  FiCode,
  FiFileText,
  FiDownload,
  FiUpload,
  FiRotateCcw,
  FiRotateCw,
  FiSettings,
  FiActivity,
  FiShield,
  FiPrinter,
  FiAlertTriangle,
  FiMoreHorizontal,
  FiColumns,
  FiMinus,
  FiSearch,
  FiLink
} from "react-icons/fi";
import "./App.css";

const WHITELIST_STORAGE_KEY = "frontplay_whitelist";
const DISCLAIMER_VERSION_KEY = "frontplay_disclaimer_version";
const LEGACY_SECURITY_ACCEPTED_KEY = "frontplay_security_accepted";
// Bump this when the disclaimer text materially changes; existing users will be re-prompted.
const DISCLAIMER_VERSION = "v1";
const SPLIT_RATIO_KEY = "frontplay_split_ratio";
const DEFAULT_SPLIT_RATIO = 45;
const MIN_SPLIT_RATIO = 20;
const MAX_SPLIT_RATIO = 75;

function loadSplitRatio() {
  try {
    const n = Number(window.localStorage.getItem(SPLIT_RATIO_KEY));
    if (Number.isFinite(n) && n >= MIN_SPLIT_RATIO && n <= MAX_SPLIT_RATIO) return n;
  } catch (_) {}
  return DEFAULT_SPLIT_RATIO;
}
// Returns the disclaimer version the user has agreed to, or null if none.
// Silently migrates the legacy boolean flag so existing users aren't re-prompted.
function loadDisclaimerVersion() {
  try {
    const v = window.localStorage.getItem(DISCLAIMER_VERSION_KEY);
    if (v) return v;
    if (window.localStorage.getItem(LEGACY_SECURITY_ACCEPTED_KEY) === "1") {
      window.localStorage.setItem(DISCLAIMER_VERSION_KEY, DISCLAIMER_VERSION);
      window.localStorage.removeItem(LEGACY_SECURITY_ACCEPTED_KEY);
      return DISCLAIMER_VERSION;
    }
  } catch (_) {}
  return null;
}
function saveDisclaimerAgreement() {
  try {
    window.localStorage.setItem(DISCLAIMER_VERSION_KEY, DISCLAIMER_VERSION);
  } catch (_) {}
}
function loadWhitelist() {
  try {
    const raw = window.localStorage.getItem(WHITELIST_STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch (e) { return {}; }
}
function saveWhitelist(map) {
  try { window.localStorage.setItem(WHITELIST_STORAGE_KEY, JSON.stringify(map)); }
  catch (e) {}
}
function getDomain(urlStr) {
  try {
    const u = new URL(urlStr, "https://frontplay.invalid/");
    if (u.hostname === "frontplay.invalid") return "";
    return u.hostname;
  } catch (e) { return ""; }
}

const getTemplates = (texts) => ({
  markdown: `# ${texts.app.markdownTemplate.title}

${texts.app.markdownTemplate.description}

## ${texts.app.markdownTemplate.featuresTitle}

- ${texts.app.markdownTemplate.boldItalic}
- ${texts.app.markdownTemplate.link}
- ${texts.app.markdownTemplate.codeBlock}

\`\`\`javascript
function hello() {
  console.log("Hello, Markdown!");
}
\`\`\`

### ${texts.app.markdownTemplate.listTitle}
1. ${texts.app.markdownTemplate.numberedList1}
2. ${texts.app.markdownTemplate.numberedList2}
   - ${texts.app.markdownTemplate.subItem1}
   - ${texts.app.markdownTemplate.subItem2}

> ${texts.app.markdownTemplate.quote1}
> ${texts.app.markdownTemplate.quote2}

---

${texts.app.markdownTemplate.closing}`,
  html: `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <style>
    body { font-family: sans-serif; color: #333; margin: 20px; }
    h1 { color: #007bff; }
    button { 
      background: #007bff; 
      color: white; 
      border: none; 
      padding: 10px 20px; 
      border-radius: 5px; 
      cursor: pointer; 
      margin: 10px 0;
    }
    button:hover { background: #0056b3; }
  </style>
</head>
<body>
  <h1>Hello, World!</h1>
  <p>${texts.app.templateDescription}</p>
  <button onclick="showMessage()">${texts.app.htmlTemplate.buttonText}</button>
  <script>
    console.log('${texts.app.templateWelcome}');
    
    function showMessage() {
      alert('${texts.app.htmlTemplate.alertMessage}');
      console.log('${texts.app.htmlTemplate.consoleMessage}');
    }
  </script>
</body>
</html>`,
  
  react: `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <script crossorigin src="https://unpkg.com/react@18/umd/react.development.js"></script>
  <script crossorigin src="https://unpkg.com/react-dom@18/umd/react-dom.development.js"></script>
  <style>
    body { font-family: sans-serif; margin: 20px; }
    button { margin: 5px; padding: 8px 16px; }
  </style>
</head>
<body>
  <div id="root"></div>
  <script>
    const { useState } = React;
    const e = React.createElement;

    function App() {
      const [count, setCount] = useState(0);

      return e('div', null,
        e('h1', null, ${JSON.stringify(texts.app.reactTemplate.title)}),
        e('p', null, ${JSON.stringify(texts.app.reactTemplate.countLabel + ' ')} + count),
        e('button', { onClick: () => setCount(count + 1) }, '+'),
        e('button', { onClick: () => setCount(count - 1) }, '-')
      );
    }

    const root = ReactDOM.createRoot(document.getElementById('root'));
    root.render(e(App));
  </script>
</body>
</html>`,

  canvas: `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <style>
    body { font-family: sans-serif; margin: 20px; }
    canvas { border: 1px solid #ccc; }
  </style>
</head>
<body>
  <h1>${texts.app.canvasTemplate.title}</h1>
  <canvas id="canvas" width="400" height="300"></canvas>
  
  <script>
    const canvas = document.getElementById('canvas');
    const ctx = canvas.getContext('2d');
    
    // ${texts.app.canvasTemplate.gradientComment}
    const gradient = ctx.createLinearGradient(0, 0, 400, 300);
    gradient.addColorStop(0, '#ff7eb3');
    gradient.addColorStop(1, '#ff758c');
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, 400, 300);
    
    // ${texts.app.canvasTemplate.circleComment}
    ctx.fillStyle = 'white';
    ctx.beginPath();
    ctx.arc(200, 150, 50, 0, Math.PI * 2);
    ctx.fill();
    
    console.log('${texts.app.canvasTemplate.consoleMessage}');
  </script>
</body>
</html>`
});

function App() {
  const { texts } = useLanguage();
  const { isAdmin } = useAuth();
  const TEMPLATES = getTemplates(texts);
  
  const [currentView, setCurrentView] = useState('landing'); // 'landing' | 'editor' | 'admin'
  const [showSecurityWarning, setShowSecurityWarning] = useState(false);
  const [agreedDisclaimerVersion, setAgreedDisclaimerVersion] = useState(loadDisclaimerVersion);
  const [code, setCode] = useState('');
  const [currentTemplate, setCurrentTemplate] = useState('html');
  const [srcDoc, setSrcDoc] = useState('');
  const [previewKey, setPreviewKey] = useState(0);
  const [layout, setLayout] = useState('vertical');
  const [splitRatio, setSplitRatio] = useState(loadSplitRatio);
  const [darkMode, setDarkMode] = useState(false);
  const [fullscreen, setFullscreen] = useState(false);
  const [consoleOutput, setConsoleOutput] = useState([]);
  const [errors, setErrors] = useState([]);
  const [networkLogs, setNetworkLogs] = useState([]);
  const [pendingConfirm, setPendingConfirm] = useState(null);
  const [whitelist, setWhitelist] = useState(loadWhitelist);
  const [showWhitelist, setShowWhitelist] = useState(false);
  const [activeTab, setActiveTab] = useState("console"); // 'console' | 'network' | 'errors'
  const [showConsole, setShowConsole] = useState(false);
  const [previewSize, setPreviewSize] = useState({ width: 600, height: 340 });
  const [previewDevice, setPreviewDevice] = useState("responsive");
  // true = preview fills the stage (default for "responsive"); false = fixed px box
  const [previewFit, setPreviewFit] = useState(true);
  const [paperSkill, setPaperSkill] = useState("none");
  const [showPrintCaution, setShowPrintCaution] = useState(false);
  const [showEditor, setShowEditor] = useState(true);
  const [mobileToolsOpen, setMobileToolsOpen] = useState(false);
  const headerRef = useRef(null);
  const iframeRef = useRef(null);
  const previewScrollRef = useRef(null);
  const previewFrameRef = useRef(null);
  const editorScrollRef = useRef(null);
  const editorRef = useRef(null);
  const splitContainerRef = useRef(null);
  const splitDraggingRef = useRef(false);
  const pendingScrollRestore = useRef(null);
  // Latest known scroll position of the (cross-origin) preview iframe, reported
  // from inside the iframe via postMessage — the parent cannot read it directly.
  const iframeScrollStateRef = useRef({ x: 0, y: 0 });
  const isFirstRender = useRef(true);
  const undoStack = useRef([]);
  const redoStack = useRef([]);
  const fileInputRef = useRef(null);
  const isComposingRef = useRef(false);
  const preCompositionCodeRef = useRef('');
  const codeRef = useRef(code);
  const whitelistRef = useRef(whitelist);
  const preFullscreenEditorRef = useRef(true);
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);

  // In-editor code search
  const [codeFindOpen, setCodeFindOpen] = useState(false);
  const [codeFindQuery, setCodeFindQuery] = useState("");
  const [codeFindIdx, setCodeFindIdx] = useState(0);
  // In-preview search
  const [previewFindOpen, setPreviewFindOpen] = useState(false);
  const [previewFindQuery, setPreviewFindQuery] = useState("");
  const [previewFindCount, setPreviewFindCount] = useState(0);
  const [previewFindCurrent, setPreviewFindCurrent] = useState(0);
  // Reverse mapping: select text in preview -> highlight matching source line(s)
  const [linkMode, setLinkMode] = useState(false);
  const [linkText, setLinkText] = useState("");
  const [linkInfo, setLinkInfo] = useState("");
  const toggleLinkMode = () => {
    const next = !linkMode;
    setLinkMode(next);
    if (!next) {
      setLinkText("");
      setLinkInfo("");
    }
    try {
      iframeRef.current?.contentWindow?.postMessage(
        { type: "fp-set-link", on: next },
        "*"
      );
    } catch (_) {}
  };

  // Link/reverse-mapping is HTML-only (source-line attributes are injected for HTML).
  // Disable + clear it whenever the template is not HTML.
  useEffect(() => {
    if (currentTemplate !== "html") {
      if (linkMode) {
        try {
          iframeRef.current?.contentWindow?.postMessage(
            { type: "fp-set-link", on: false },
            "*"
          );
        } catch (_) {}
      }
      setLinkMode(false);
      setLinkText("");
      setLinkMatches([]);
      setLinkInfo("");
    }
  }, [currentTemplate]);

  useEffect(() => {
    codeRef.current = code;
  }, [code]);

  useEffect(() => {
    whitelistRef.current = whitelist;
    saveWhitelist(whitelist);
  }, [whitelist]);

  useEffect(() => {
    if (!mobileToolsOpen) return undefined;
    const onDoc = (e) => {
      if (headerRef.current && !headerRef.current.contains(e.target)) {
        setMobileToolsOpen(false);
      }
    };
    const onKey = (e) => {
      if (e.key === "Escape") setMobileToolsOpen(false);
    };
    const onResize = () => {
      if (window.innerWidth > 768) setMobileToolsOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("touchstart", onDoc, { passive: true });
    document.addEventListener("keydown", onKey);
    window.addEventListener("resize", onResize);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("touchstart", onDoc);
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("resize", onResize);
    };
  }, [mobileToolsOpen]);

  // テンプレートから言語を決定する関数
  const getLanguageFromTemplate = (template) => {
    switch (template) {
      case 'markdown':
        return 'markdown';
      default:
        return 'html';
    }
  };

  const language = getLanguageFromTemplate(currentTemplate);

  useEffect(() => {
    if (darkMode) {
      document.body.classList.add('dark-mode');
    } else {
      document.body.classList.remove('dark-mode');
    }
  }, [darkMode]);

  // 初期化フラグのみ設定
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
    }
  }, []);

  const captureScrollState = () => {
    const state = {
      editorScrollTop: editorScrollRef.current?.scrollTop ?? 0,
      editorScrollLeft: editorScrollRef.current?.scrollLeft ?? 0,
      previewScrollTop: previewScrollRef.current?.scrollTop ?? 0,
      previewScrollLeft: previewScrollRef.current?.scrollLeft ?? 0,
      // The preview iframe is sandboxed (opaque origin); the parent cannot read
      // its scrollY cross-origin (it returns 0). Use the value reported by the
      // in-iframe scroll-sync script instead.
      iframeScrollTop: iframeScrollStateRef.current.y,
      iframeScrollLeft: iframeScrollStateRef.current.x,
    };

    return state;
  };

  const restoreScrollState = (state) => {
    if (!state) return;

    if (editorScrollRef.current) {
      editorScrollRef.current.scrollTop = state.editorScrollTop;
      editorScrollRef.current.scrollLeft = state.editorScrollLeft;
    }

    if (previewScrollRef.current) {
      previewScrollRef.current.scrollTop = state.previewScrollTop;
      previewScrollRef.current.scrollLeft = state.previewScrollLeft;
    }

    // The preview iframe is cross-origin (sandboxed), so the parent cannot set
    // its scrollY directly. Ask the in-iframe scroll-sync script to do it.
    try {
      iframeRef.current?.contentWindow?.postMessage(
        { type: "fp-set-scroll", x: state.iframeScrollLeft, y: state.iframeScrollTop },
        "*"
      );
    } catch {
      // ignore
    }
  };

  const scheduleScrollRestore = (state) => {
    requestAnimationFrame(() => {
      restoreScrollState(state);
      requestAnimationFrame(() => restoreScrollState(state));
    });
  };

  const handleIframeLoad = () => {
    if (pendingScrollRestore.current) {
      const state = pendingScrollRestore.current;
      restoreScrollState(state);
      scheduleScrollRestore(state);
      // Content (scripts/images/fonts) may still be growing after `load`, so the
      // iframe can't scroll to the saved position yet. Re-issue the restore a few
      // times to land correctly once layout settles.
      [60, 200, 450, 800].forEach((delay) =>
        setTimeout(() => restoreScrollState(state), delay)
      );
      pendingScrollRestore.current = null;
    }
    // Re-apply an active preview search after the (re)loaded iframe is ready.
    if (previewFindOpen && previewFindQuery) {
      try {
        iframeRef.current?.contentWindow?.postMessage(
          { type: "preview-find", query: previewFindQuery, dir: 0 },
          "*"
        );
      } catch (_) {}
    }
    // Re-apply reverse-mapping (link) mode after the (re)loaded iframe is ready.
    if (linkMode) {
      try {
        iframeRef.current?.contentWindow?.postMessage(
          { type: "fp-set-link", on: true },
          "*"
        );
      } catch (_) {}
    }
  };

  const executeCode = (codeToExecute, templateType, { preserveScroll = true } = {}) => {
    if (preserveScroll) {
      pendingScrollRestore.current = captureScrollState();
    } else {
      pendingScrollRestore.current = null;
    }

    // 1) CSP meta (must be the FIRST child of <head> so it's active before any
    //    user script runs). Inbound sources allowed; outbound connect-src /
    //    form-action 'none' as a defense-in-depth final wall.
    // 2) Security + monitoring + console-capture script: must also run before
    //    user code so monkey-patches of fetch/XHR/WebSocket/etc. are in place
    //    before user localStorage-side caching tricks (e.g. `let f = fetch`)
    //    could fire during initial parse.
    const cspMeta = buildCspMetaTag();
    const securityScript = buildNetworkSecurityScript();
    const previewFindScript = buildPreviewFindScript();
    const selectionLinkScript = buildSelectionLinkScript();
    const scrollSyncScript = buildScrollSyncScript();

    // Markdown is converted to a standalone HTML document here so it flows
    // through the same sandboxed iframe + CSP + network hooks as other
    // templates. This lets AI inject <style> / inline style for requests like
    // "make the text smaller" (pure Markdown has no font-size syntax).
    let finalCode =
      templateType === "markdown"
        ? buildMarkdownDocument(codeToExecute, { dark: darkMode })
        : codeToExecute;

    // HTML template only: embed source line numbers so the preview can report the
    // EXACT source line that produced a rendered element (true reverse mapping).
    if (templateType === "html") {
      finalCode = injectSourceLineAttrs(finalCode);
    }

    // Inject the CSP <meta> + security <script> as the first children of <head>.
    // `replace` honors the first occurrence of a full-element <head ...> tag.
    // Falls back to injecting just before the first <script> tag when no <head>
    // exists, and finally prepends the whole HTML if neither is present.
    const cspAndSecurity = cspMeta + securityScript + previewFindScript + selectionLinkScript + scrollSyncScript;

    if (/<head[^>]*>/i.test(finalCode)) {
      // Insert right after the opening <head ...> tag.
      finalCode = finalCode.replace(/<head[^>]*>/i, (match) => match + cspAndSecurity);
    } else if (/<html[^>]*>/i.test(finalCode)) {
      // No <head>; insert one with our bootstrap content after <html ...>.
      finalCode = finalCode.replace(/<html[^>]*>/i, (match) => match + `<head>${cspAndSecurity}</head>`);
    } else if (/<script/i.test(finalCode)) {
      // Bare code with scripts: put our security script before the first one.
      finalCode = cspAndSecurity + finalCode;
    } else {
      // Truly minimal fragment: prepend anyway so hooks exist in case the
      // browser auto-wraps it into <body>.
      finalCode = cspAndSecurity + finalCode;
    }

    // Reset any stale confirmation that was awaiting an answer from a prior run.
    setPendingConfirm(null);

    setSrcDoc(finalCode);

    if (preserveScroll && pendingScrollRestore.current) {
      scheduleScrollRestore(pendingScrollRestore.current);
    }
  };

  const syncPreview = (codeToRun) => {
    if (srcDoc) {
      setConsoleOutput([]);
      setErrors([]);
      setNetworkLogs([]);
      setPendingConfirm(null);
      executeCode(codeToRun, currentTemplate);
    }
  };

  const handleRun = () => {
    setConsoleOutput([]);
    setErrors([]);
    setNetworkLogs([]);
    setPendingConfirm(null);
    executeCode(code, currentTemplate);
  };

  const handleAiApply = (newCode) => {
    if (typeof newCode !== "string" || newCode === codeRef.current) return;
    undoStack.current.push(codeRef.current);
    redoStack.current = [];
    setCanUndo(true);
    setCanRedo(false);
    setCode(newCode);
    // Always re-run so the user can iterate against the live preview.
    setConsoleOutput([]);
    setErrors([]);
    setNetworkLogs([]);
    setPendingConfirm(null);
    executeCode(newCode, currentTemplate);
  };

  // User decided in the confirm modal: 'allow-once' | 'allow-always' | 'deny'.
  const handleNetworkConfirm = (decision) => {
    const req = pendingConfirm;
    if (!req) return;
    if (decision === 'allow-always') {
      const domain = getDomain(req.url);
      if (domain) {
        setWhitelist(prev => ({
          ...prev,
          [domain]: { allowSend: true, allowBeacon: false, addedAt: Date.now() }
        }));
      }
    }
    try {
      iframeRef.current?.contentWindow.postMessage(
        { type: 'network-confirm-response', id: req.id, decision },
        '*'
      );
    } catch (e) {}
    setPendingConfirm(null);
  };

  const handleWhitelistChange = (next) => {
    setWhitelist(next);
  };

  useEffect(() => {
    const clampRatio = (value) =>
      Math.max(MIN_SPLIT_RATIO, Math.min(MAX_SPLIT_RATIO, value));

    const isSideBySide = (el) => {
      const style = getComputedStyle(el);
      // Vertical (and stacked horizontal ≤1024px) use display:grid — flexDirection
      // still reports the initial "row", so only treat true row flex as side-by-side.
      return style.display === 'flex' && style.flexDirection === 'row';
    };

    const updateFromClient = (clientX, clientY) => {
      const el = splitContainerRef.current;
      if (!el || !splitDraggingRef.current) return;
      const rect = el.getBoundingClientRect();
      if (rect.width <= 0 || rect.height <= 0) return;
      const next = isSideBySide(el)
        ? ((clientX - rect.left) / rect.width) * 100
        : ((clientY - rect.top) / rect.height) * 100;
      setSplitRatio(clampRatio(next));
    };

    const onMove = (e) => updateFromClient(e.clientX, e.clientY);
    const onTouchMove = (e) => {
      if (!splitDraggingRef.current || !e.touches[0]) return;
      e.preventDefault();
      updateFromClient(e.touches[0].clientX, e.touches[0].clientY);
    };
    const onUp = () => {
      if (!splitDraggingRef.current) return;
      splitDraggingRef.current = false;
      document.body.classList.remove('split-resizing', 'split-resizing-row');
      setSplitRatio((ratio) => {
        try {
          window.localStorage.setItem(SPLIT_RATIO_KEY, String(ratio));
        } catch (_) {}
        return ratio;
      });
    };

    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    window.addEventListener('touchmove', onTouchMove, { passive: false });
    window.addEventListener('touchend', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      window.removeEventListener('touchmove', onTouchMove);
      window.removeEventListener('touchend', onUp);
    };
  }, [layout]);

  const startSplitDrag = (e) => {
    e.preventDefault();
    splitDraggingRef.current = true;
    document.body.classList.add('split-resizing');
    const el = splitContainerRef.current;
    if (el) {
      const style = getComputedStyle(el);
      const sideBySide = style.display === 'flex' && style.flexDirection === 'row';
      if (!sideBySide) document.body.classList.add('split-resizing-row');
    }
  };

  useEffect(() => {
    const handleMessage = (event) => {
      // srcdoc iframe with sandbox="allow-scripts" has an opaque origin, so
      // event.origin is the string "null". Reject anything else (prevents
      // spoofed log entries from other windows/iframes).
      if (event.origin !== 'null') return;
      const data = event.data;
      if (!data || typeof data !== 'object') return;

      if (data.type === 'fp-scroll') {
        // Track the preview iframe's internal scroll position (reported from
        // inside the sandboxed iframe, since the parent can't read it directly).
        iframeScrollStateRef.current = {
          x: typeof data.x === 'number' ? data.x : 0,
          y: typeof data.y === 'number' ? data.y : 0
        };
        return;
      }

      if (data.type === 'console') {
        setConsoleOutput(prev => [...prev, {
          method: data.method,
          args: data.args,
          timestamp: new Date().toLocaleTimeString()
        }]);
        setShowConsole(true);
      } else if (data.type === 'error') {
        setErrors(prev => [...prev, {
          message: data.message,
          line: data.line,
          column: data.column,
          timestamp: new Date().toLocaleTimeString()
        }]);
        setShowConsole(true);
        setActiveTab('errors');
      } else if (data.type === 'network-log' || data.type === 'csp-violation') {
        const entry = data.type === 'csp-violation'
          ? {
              method: 'CSP',
              url: data.blockedURI || '',
              direction: 'outbound',
              status: 'blocked',
              api: 'csp',
              riskBadge: false,
              badge: null,
              violatedDirective: data.violatedDirective,
              timestamp: Date.now()
            }
          : { ...data, timestamp: data.timestamp || Date.now() };
        setNetworkLogs(prev => [...prev, entry]);
        setShowConsole(true);
        if (entry.status === 'blocked' || entry.status === 'pending') {
          setActiveTab('network');
        }
      } else if (data.type === 'network-confirm-request') {
        // Auto-allow if domain is whitelisted with allowSend (or allowBeacon);
        // otherwise surface the modal for the user to decide.
        const domain = getDomain(data.url);
        const wl = whitelistRef.current[domain];
        if (wl && wl.allowSend) {
          setNetworkLogs(prev => [...prev, {
            method: data.method, url: data.url, direction: 'outbound',
            status: 'allowed', api: data.api, riskBadge: false, badge: null,
            timestamp: Date.now()
          }]);
          try {
            iframeRef.current?.contentWindow.postMessage(
              { type: 'network-confirm-response', id: data.id, decision: 'allow-once' },
              '*'
            );
          } catch (e) {}
          return;
        }
        setPendingConfirm({
          id: data.id,
          api: data.api,
          method: data.method,
          url: data.url,
          body: data.body,

          headers: data.headers
        });
      } else if (data.type === 'preview-find-result') {
        setPreviewFindCount(data.count || 0);
        setPreviewFindCurrent(data.index || 0);
      } else if (data.type === 'fp-preview-selection') {
        const t = (data.text || '').trim();
        if (!t) {
          setLinkText("");
          setLinkInfo("");
          setLinkMatches([]);
          return;
        }
        const src = codeRef.current || "";
        const line = typeof data.line === "number" ? data.line : null;
        if (line && line >= 1 && line <= src.split("\n").length) {
          // Exact reverse mapping: highlight ONLY the source line that produced this
          // rendered element (not a fuzzy text search).
          const lineText = src.split("\n")[line - 1] || "";
          const lineStart = src.split("\n").slice(0, line - 1).join("\n").length + (line > 1 ? 1 : 0);
          setLinkText(lineText);
          setLinkMatches([lineStart]);
          setLinkInfo(texts.app.linkMatched.replace('{line}', line).replace('{count}', 1));
          scrollEditorToMatch([lineStart], lineText.length, 0, false, false);
        } else {
          // Fallback (dynamic content / no source-line attribute): best-effort text search.
          const matches = findMatches(src, t);
          setLinkText(t);
          setLinkMatches(matches);
          if (matches.length === 0) {
            setLinkInfo(
              (texts?.app?.linkNoMatch) || "ソースに該当なし（動的生成の可能性）"
            );
          } else {
            const fline = src.slice(0, matches[0]).split("\n").length;
            setLinkInfo(texts.app.linkMatched.replace('{line}', fline).replace('{count}', matches.length));
          }
          scrollEditorToMatch(matches, t.length, 0, false, false);
        }
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  const handleCodeChange = (newCode) => {
    if (!isComposingRef.current) {
      undoStack.current.push(codeRef.current);
      redoStack.current = [];
      setCanUndo(true);
      setCanRedo(false);
    }
    setCode(newCode);
  };

  const handleEditorCompositionStart = () => {
    isComposingRef.current = true;
    preCompositionCodeRef.current = codeRef.current;
  };

  const handleEditorCompositionEnd = () => {
    // compositionend が keydown より先に発火するブラウザ向けの猶予
    isComposingRef.current = true;
    setTimeout(() => {
      isComposingRef.current = false;
      if (preCompositionCodeRef.current !== codeRef.current) {
        undoStack.current.push(preCompositionCodeRef.current);
        redoStack.current = [];
        setCanUndo(true);
        setCanRedo(false);
      }
    }, 0);
  };

  const handleUndo = () => {
    if (undoStack.current.length > 0) {
      redoStack.current.push(codeRef.current);
      const previous = undoStack.current.pop();
      setCode(previous);
      setCanUndo(undoStack.current.length > 0);
      setCanRedo(true);
      syncPreview(previous);
    }
  };

  const handleRedo = () => {
    if (redoStack.current.length > 0) {
      undoStack.current.push(codeRef.current);
      const next = redoStack.current.pop();
      setCode(next);
      setCanUndo(true);
      setCanRedo(redoStack.current.length > 0);
      syncPreview(next);
    }
  };

  const handleEditorKeyDown = (evn) => {
    if (isComposingRef.current || evn.nativeEvent.isComposing || evn.keyCode === 229) {
      return false;
    }
    if ((evn.ctrlKey || evn.metaKey) && evn.key === 'z' && !evn.shiftKey) {
      evn.preventDefault();
      handleUndo();
      return false;
    }
    if ((evn.ctrlKey || evn.metaKey) && (evn.key === 'y' || (evn.key === 'z' && evn.shiftKey))) {
      evn.preventDefault();
      handleRedo();
      return false;
    }
    if ((evn.ctrlKey || evn.metaKey) && evn.key.toLowerCase() === 'f') {
      evn.preventDefault();
      setCodeFindOpen(true);
      return false;
    }
    if (evn.key === 'Escape' && codeFindOpen) {
      evn.preventDefault();
      setCodeFindOpen(false);
      return false;
    }
  };

  const findMatches = (src, q) => {
    if (!q) return [];
    const low = src.toLowerCase();
    const ql = q.toLowerCase();
    const res = [];
    let i = 0;
    while ((i = low.indexOf(ql, i)) !== -1) {
      res.push(i);
      i += ql.length;
    }
    return res;
  };

  const codeMatches = useMemo(
    () => findMatches(code, codeFindQuery),
    [code, codeFindQuery]
  );
  const [linkMatches, setLinkMatches] = useState([]);

  const scrollEditorToMatch = (matches, qLen, idx, shouldFocus = false, selectText = true) => {
    const ta = editorRef.current;
    if (!ta || matches.length === 0) return;
    const len = matches.length;
    const i = ((idx % len) + len) % len;
    const start = matches[i];
    const end = start + qLen;
    if (shouldFocus) ta.focus();
    // Note: setSelectionRange only *selects* text in the editor; it never mutates
    // the value. We still avoid it for the reverse-mapping link feature so the code
    // doesn't look "edited" (a stray selection highlight over the source).
    if (selectText) {
      try { ta.setSelectionRange(start, end); } catch (_) {}
    }
    const line = (codeRef.current || "").slice(0, start).split("\n").length - 1;
    const lh = parseFloat(getComputedStyle(ta).lineHeight) || 18;
    const cont = ta.closest(".code-editor-container");
    if (cont) {
      cont.scrollTop = Math.max(0, line * lh - cont.clientHeight / 2);
    } else {
      ta.scrollTop = Math.max(0, line * lh - ta.clientHeight / 2);
    }
  };

  const focusCodeMatch = (idx, shouldFocus = true) => {
    scrollEditorToMatch(codeMatches, codeFindQuery.length, idx, shouldFocus);
  };

  // --- In-editor find highlight overlay (does not steal focus from the find input) ---
  const codeFindOverlayRef = useRef(null);
  const escapeHtml = (s) =>
    s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  const buildCodeFindHtml = (src, matches, qLen, current) => {
    if (!qLen) return escapeHtml(src);
    let html = "";
    let pos = 0;
    matches.forEach((start, i) => {
      html += escapeHtml(src.slice(pos, start));
      const txt = escapeHtml(src.slice(start, start + qLen));
      html += `<mark class="code-find-hit${i === current ? " code-find-current" : ""}">${txt}</mark>`;
      pos = start + qLen;
    });
    html += escapeHtml(src.slice(pos));
    return html;
  };

  useLayoutEffect(() => {
    const ta = editorRef.current;
    const ov = codeFindOverlayRef.current;
    if (!ta || !ov) return;
    if ((!codeFindOpen || !codeFindQuery) && (!linkMode || !linkText)) return;
    const cs = getComputedStyle(ta);
    // Mirror the textarea's box model EXACTLY so the (transparent) duplicated text
    // and <mark> highlights line up pixel-perfect with the rendered code and never
    // re-wrap (which would otherwise look like the code got garbled/moved).
    ov.style.boxSizing = cs.boxSizing;
    ov.style.fontFamily = cs.fontFamily;
    ov.style.fontSize = cs.fontSize;
    ov.style.fontWeight = cs.fontWeight;
    ov.style.fontStyle = cs.fontStyle;
    ov.style.lineHeight = cs.lineHeight;
    ov.style.letterSpacing = cs.letterSpacing;
    ov.style.wordSpacing = cs.wordSpacing;
    ov.style.textIndent = cs.textIndent;
    ov.style.whiteSpace = cs.whiteSpace;
    ov.style.overflowWrap = cs.overflowWrap;
    ov.style.wordBreak = cs.wordBreak;
    ov.style.textAlign = cs.textAlign;
    ov.style.tabSize = cs.tabSize;
    ov.style.padding = cs.padding;
    ov.style.border = cs.border;
    ov.style.margin = "0";
    const cont = ta.closest(".code-editor-container");
    if (!cont) return;
    // Align the overlay EXACTLY to the textarea's own box (not its .w-tc-editor
    // wrapper, which carries its own border/padding and would offset the text).
    const taRect = ta.getBoundingClientRect();
    const contRect = cont.getBoundingClientRect();
    // Absolute `top/left` are relative to the container's UNSCROLLED origin, but
    // getBoundingClientRect is viewport-relative (shifts when the container scrolls).
    // Add the scroll offsets back so the overlay tracks the textarea exactly.
    ov.style.top = `${taRect.top - contRect.top + cont.scrollTop}px`;
    ov.style.left = `${taRect.left - contRect.left + cont.scrollLeft}px`;
    ov.style.width = `${taRect.width}px`;
    ov.style.height = `${taRect.height}px`;
    const sync = () => {
      ov.scrollTop = ta.scrollTop;
      ov.scrollLeft = ta.scrollLeft;
    };
    sync();
    ta.addEventListener("scroll", sync);
    return () => ta.removeEventListener("scroll", sync);
  }, [codeFindOpen, codeFindQuery, codeFindIdx, codeMatches, linkMode, linkText, linkMatches]);

  const onCodeFindQueryChange = (v) => {
    setCodeFindQuery(v);
    setCodeFindIdx(0);
  };
  const codeFindNext = () => focusCodeMatch(codeFindIdx + 1);
  const codeFindPrev = () => focusCodeMatch(codeFindIdx - 1);

const doPreviewFind = (q, dir) => {
    const w = iframeRef.current?.contentWindow;
    if (!w || !q) return;
    try {
      w.postMessage({ type: "preview-find", query: q, dir }, "*");
    } catch (_) {}
  };
  const onPreviewFindQueryChange = (v) => {
    setPreviewFindQuery(v);
    setPreviewFindCurrent(0);
    if (v) doPreviewFind(v, 0); else setPreviewFindCount(0);
  };
  const previewFindNext = () => doPreviewFind(previewFindQuery, 1);
  const previewFindPrev = () => doPreviewFind(previewFindQuery, -1);

  useEffect(() => {
    if (codeFindOpen && codeFindQuery) focusCodeMatch(0, false);
  }, [codeFindQuery, codeFindOpen]);

  const handleClearAll = () => {
    const hasContent =
      code.trim() ||
      consoleOutput.length > 0 ||
      errors.length > 0 ||
      networkLogs.length > 0;
    if (hasContent) {
      const msg =
        texts.app.clearConfirm ||
        "エディタ・プレビュー・コンソールをすべてクリアします。よろしいですか？";
      if (!window.confirm(msg)) return;
    }
    undoStack.current = [];
    redoStack.current = [];
    setCanUndo(false);
    setCanRedo(false);
    setCode("");
    setSrcDoc("");
    setPreviewKey(0);
    setConsoleOutput([]);
    setErrors([]);
    setNetworkLogs([]);
    setPendingConfirm(null);
  };

  const handleSelectMode = (mode) => {
    if (mode === currentTemplate) return;
    // 既存のコードがある場合は、種別切替でコードがクリアされる旨を確認。
    // キャンセル時はテンプレートを切り替えず、選択も元に戻る。
    if (codeRef.current.trim()) {
      const msg =
        texts.app.templateSwitchClearConfirm ||
        "コード種別を切り替えると、現在のコードはクリアされます。よろしいですか？";
      if (!window.confirm(msg)) return;
    }
    undoStack.current = [];
    redoStack.current = [];
    setCanUndo(false);
    setCanRedo(false);
    setCurrentTemplate(mode);
    setCode("");
    setSrcDoc("");
    pendingScrollRestore.current = null;
    setConsoleOutput([]);
    setErrors([]);
    setNetworkLogs([]);
    setPendingConfirm(null);
  };

  const handleLoadSample = () => {
    if (!currentTemplate) return;
    const sample = TEMPLATES[currentTemplate];
    if (!sample) return;
    undoStack.current.push(codeRef.current);
    redoStack.current = [];
    setCanUndo(true);
    setCanRedo(false);
    setCode(sample);
    setConsoleOutput([]);
    setErrors([]);
    setNetworkLogs([]);
    setPendingConfirm(null);
    executeCode(sample, currentTemplate, { preserveScroll: false });
  };

  // Preview-only immersive mode: iframe fills the viewport like a real browser tab.
  // Distinct from "hide editor" which still shows app chrome / preview chrome.
  const exitPreviewFullscreen = () => {
    setFullscreen(false);
    setShowEditor(preFullscreenEditorRef.current);
  };

  const toggleFullscreen = () => {
    if (fullscreen) {
      exitPreviewFullscreen();
      return;
    }
    preFullscreenEditorRef.current = showEditor;
    setShowEditor(false);
    setShowConsole(false);
    setPreviewFit(true);
    setPreviewDevice("responsive");
    setFullscreen(true);
  };

  useEffect(() => {
    if (!fullscreen) return undefined;
    const onKeyDown = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        exitPreviewFullscreen();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [fullscreen]);

  // Single entry point to the editor: shows the disclaimer unless the user has
  // already agreed to the current version. Triggered only by explicit user
  // action ("今すぐ始める"), never by login/session restore.
  const enterEditor = () => {
    if (agreedDisclaimerVersion !== DISCLAIMER_VERSION) {
      setShowSecurityWarning(true);
    } else {
      setCurrentView('editor');
    }
  };

  const handleGetStarted = () => {
    enterEditor();
  };

  const handleSecurityAccept = () => {
    saveDisclaimerAgreement();
    setAgreedDisclaimerVersion(DISCLAIMER_VERSION);
    setShowSecurityWarning(false);
    setCurrentView('editor');
  };

  const handleSecurityDismiss = () => {
    setShowSecurityWarning(false);
  };

  const handleBackToLanding = () => {
    if (code.trim()) {
      const msg =
        texts.app.leaveEditorConfirm ||
        "エディタを離れます。コードはこのセッション中は保持されます。よろしいですか？";
      if (!window.confirm(msg)) return;
    }
    setCurrentView('landing');
  };

  const handleOpenAdmin = () => {
    setCurrentView('admin');
  };

  const handleDeviceChange = (e) => {
    const id = e.target.value;
    const preset = getDevicePreset(id);
    setPreviewDevice(id);
    if (id === "responsive" || (preset.width == null && preset.height == null)) {
      setPreviewFit(true);
      return;
    }
    if (preset.width != null && preset.height != null) {
      setPreviewFit(false);
      setPreviewSize({ width: preset.width, height: preset.height });
    }
  };

  const pinPreviewSize = (width, height) => {
    setPreviewFit(false);
    setPreviewSize({
      width: Math.max(200, Math.min(4000, width)),
      height: Math.max(120, Math.min(4000, height)),
    });
    setPreviewDevice("responsive");
  };

  // Keep W×H labels honest while the preview fills the pane (split-bar only layout).
  useEffect(() => {
    if (!previewFit) return undefined;
    const el = previewFrameRef.current;
    if (!el || typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver((entries) => {
      const cr = entries[0]?.contentRect;
      if (!cr || cr.width < 1 || cr.height < 1) return;
      setPreviewSize({
        width: Math.round(cr.width),
        height: Math.round(cr.height),
      });
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [previewFit, showConsole]);

  const handleDownload = () => {
    let content =
      currentTemplate === 'markdown'
        ? buildMarkdownDocument(code, { dark: darkMode })
        : code;
    const filename = 'index.html';

    content = buildStandaloneHtml(content, null);

    const blob = new Blob([content], { type: 'text/html;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Same HTML as download — open in a new window and print (matches "download then browser print").
  const handlePrint = () => {
    let content;
    if (currentTemplate === 'markdown') {
      content = buildStandaloneHtml(
        buildMarkdownDocument(code, { dark: darkMode }),
        null
      );
    } else {
      content = buildStandaloneHtml(ensureCharset(code), null);
    }
    // Auto-print after load so canvas/scripts can paint first.
    if (/<\/body>/i.test(content)) {
      content = content.replace(
        /<\/body>/i,
        `<script>(function(){function go(){try{window.focus();window.print();}catch(e){}}if(document.readyState==='complete')setTimeout(go,200);else window.addEventListener('load',function(){setTimeout(go,200);});})();<\/script>\n</body>`
      );
    } else {
      content += `<script>(function(){function go(){try{window.focus();window.print();}catch(e){}}if(document.readyState==='complete')setTimeout(go,200);else window.addEventListener('load',function(){setTimeout(go,200);});})();<\/script>`;
    }

    const blob = new Blob([content], { type: 'text/html;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const w = window.open(url, '_blank');
    if (!w) {
      URL.revokeObjectURL(url);
      return;
    }
    // Revoke after the print window has had time to load the blob.
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  };

  // Warn when print CSS would drop on-screen margins, and offer a one-click fix.
  const printIssues = useMemo(
    () => lintPrintIssues(code).concat(lintPrintOverflow(code)),
    [code]
  );

  const handleFixPrint = () => {
    const fixed = fixPrintOverflow(fixPrintIssues(code));
    if (fixed && fixed !== code) {
      handleAiApply(fixed);
    }
    setShowPrintCaution(false);
  };

  const handleImportClick = () => {
    fileInputRef.current?.click();
  };

  const handleImportFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (event) => {
      const content = event.target.result;
      undoStack.current.push(codeRef.current);
      redoStack.current = [];
      setCanUndo(true);
      setCanRedo(false);
      setCode(content);
      if (/\.(md|markdown)$/i.test(file.name)) {
        setCurrentTemplate('markdown');
      } else {
        setCurrentTemplate('html');
      }
      setConsoleOutput([]);
      setErrors([]);
      setNetworkLogs([]);
      setPendingConfirm(null);
    };
    reader.readAsText(file);
    e.target.value = '';
  };

  // Show landing page
  if (currentView === 'landing') {
    return (
      <>
        <LandingPage onGetStarted={handleGetStarted} isAdmin={isAdmin} onOpenAdmin={handleOpenAdmin} />
        {showSecurityWarning && (
          <SecurityWarning 
            onAccept={handleSecurityAccept}
            onDismiss={handleSecurityDismiss}
          />
        )}
      </>
    );
  }

  // Show admin panel
  if (currentView === 'admin') {
    return <AdminPanel onBack={handleBackToLanding} />;
  }

  // Show editor
  return (
    <div className={`app ${darkMode ? 'dark' : ''} ${fullscreen ? 'fullscreen preview-fullscreen' : ''}`}>
      <SEO
        title={`${texts.app.title} | ${texts.app.subtitle}`}
        description={texts.app.codePlaceholder}
        canonical="https://frontplay.contextaflex.com"
      />
      {fullscreen && (
        <button
          type="button"
          className="preview-fullscreen-exit"
          onClick={toggleFullscreen}
          title={texts.app.fullscreenExitTitle || texts.app.fullscreenTitle}
          aria-label={texts.app.fullscreenExitTitle || texts.app.fullscreenTitle}
        >
          <FiMinimize />
        </button>
      )}
      <div className="container">
        <div className="header" ref={headerRef}>
          <div className="title">
            <button
              type="button"
              className="title-link"
              onClick={handleBackToLanding}
              title={texts.app.backToLandingTitle}
            >
              <h1>{texts.app.title}</h1>
            </button>
          </div>
          <button
            type="button"
            className={`mobile-tools-toggle${mobileToolsOpen ? ' active' : ''}`}
            onClick={() => setMobileToolsOpen((v) => !v)}
            aria-expanded={mobileToolsOpen}
            aria-controls="workspace-tools"
            title={texts.app.toolsMenuTitle || 'ツール'}
            aria-label={texts.app.toolsMenuTitle || 'ツール'}
          >
            <FiMoreHorizontal />
          </button>
          <div
            id="workspace-tools"
            className={`controls${mobileToolsOpen ? ' is-open' : ''}`}
          >
            <LanguageToggle />
            <div className="template-selector">
              <label>{texts.app.templateLabel}</label>
              <select onChange={(e) => handleSelectMode(e.target.value)} value={currentTemplate}>
                <option value="html">{texts.app.templateBasicHtml}</option>
                <option value="react">{texts.app.templateReact}</option>
                <option value="canvas">{texts.app.templateCanvas}</option>
                <option value="markdown">Markdown</option>
              </select>
              <button
                type="button"
                className="sample-btn"
                onClick={handleLoadSample}
                disabled={!currentTemplate}
                title={texts.app.sampleLoad}
              >
                <FiFileText /> <span className="btn-label">{texts.app.sampleLoad}</span>
              </button>
            </div>
            <div className="mode-toggles">
              {isAdmin && (
                <button
                  className="mode-btn"
                  onClick={handleOpenAdmin}
                  title={(texts.admin && texts.admin.title) || "Admin"}
                >
                  <FiShield />
                </button>
              )}
              <button 
                className={`mode-btn ${darkMode ? 'active' : ''}`}
                onClick={() => setDarkMode(!darkMode)}
                title={texts.app.darkModeTitle}
              >
                {darkMode ? <FiSun /> : <FiMoon />}
              </button>
              <button 
                className={`mode-btn ${fullscreen ? 'active' : ''}`}
                onClick={toggleFullscreen}
                title={texts.app.fullscreenTitle}
              >
                {fullscreen ? <FiMinimize /> : <FiMaximize />}
              </button>
              <button
                className={`mode-btn ${!showEditor ? 'active' : ''}`}
                onClick={() => setShowEditor(prev => !prev)}
                title={showEditor ? texts.app.hideEditorTitle : texts.app.showEditorTitle}
              >
                <FiCode />
              </button>
            </div>
            <div className="layout-toggle">
              <button
                className={layout === 'vertical' ? 'layout-btn active' : 'layout-btn'}
                onClick={() => setLayout('vertical')}
                title={texts.app.verticalSplit}
              >
                <FiMinus className="layout-btn-icon" aria-hidden />
                <span className="btn-label">{texts.app.verticalSplit}</span>
              </button>
              <button
                className={layout === 'horizontal' ? 'layout-btn active' : 'layout-btn'}
                onClick={() => {
                  setLayout('horizontal');
                  setPreviewFit(true);
                  setPreviewDevice('responsive');
                }}
                title={texts.app.horizontalSplit}
              >
                <FiColumns className="layout-btn-icon" aria-hidden />
                <span className="btn-label">{texts.app.horizontalSplit}</span>
              </button>
            </div>
          </div>
          <div className="header-end">
            <AuthControl />
          </div>
        </div>

        <div
          ref={splitContainerRef}
          className={`${layout === 'horizontal' ? 'split-container horizontal' : 'split-container vertical'}${showEditor ? '' : ' editor-hidden'}`}
          style={{
            '--split-editor': splitRatio,
            '--split-preview': 100 - splitRatio,
            '--split-editor-fr': `${splitRatio}fr`,
            '--split-preview-fr': `${100 - splitRatio}fr`,
          }}
        >
          {showEditor && <div className="editor-pane">
            <div className="editor-header">
              <h2>{texts.app.codeEditor}</h2>
              <div className="editor-actions">
                <button className="run-btn" onClick={handleRun} title={texts.app.run}>
                  <FiPlay /> <span className="btn-label">{texts.app.run}</span>
                </button>
                <button className="undo-btn" onClick={handleUndo} disabled={!canUndo} title={texts.app.undoTitle}>
                  <FiRotateCcw />
                </button>
                <button className="redo-btn" onClick={handleRedo} disabled={!canRedo} title={texts.app.redoTitle}>
                  <FiRotateCw />
                </button>
                <button
                  type="button"
                  className={`find-btn${codeFindOpen ? ' active' : ''}`}
                  onClick={() => setCodeFindOpen((v) => !v)}
                  title={texts.app.findTitle + ' (Ctrl+F)'}
                  aria-label={texts.app.find}
                >
                  <FiSearch /> <span className="btn-label">{texts.app.find}</span>
                </button>
                <button className="import-btn" onClick={handleImportClick} title={texts.app.import}>
                  <FiUpload /> <span className="btn-label">{texts.app.import}</span>
                </button>
                <button className="download-btn" onClick={handleDownload} disabled={!code} title={texts.app.download}>
                  <FiDownload /> <span className="btn-label">{texts.app.download}</span>
                </button>
                <button className="clear-btn" onClick={handleClearAll} title={texts.app.clear}>
                  <FiX /> <span className="btn-label">{texts.app.clear}</span>
                </button>
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleImportFile}
                  accept=".html,.htm,.md,.markdown,.txt,text/html,text/markdown,text/plain"
                  style={{ display: 'none' }}
                />
              </div>
            </div>
            
            <div className="code-editor-container" ref={editorScrollRef}>
              {codeFindOpen && (
                <FindBar
                  className="find-bar--code"
                  texts={texts}
                  query={codeFindQuery}
                  onQueryChange={onCodeFindQueryChange}
                  onNext={codeFindNext}
                  onPrev={codeFindPrev}
                  matchCount={codeMatches.length}
                  currentIndex={codeFindIdx + 1}
                  onClose={() => setCodeFindOpen(false)}
                  placeholder={texts.app.findPlaceholder}
                />
              )}
              {(codeFindOpen && codeFindQuery) || (linkMode && linkText) ? (
                (() => {
                  const useLinked = !codeFindOpen && linkMode && linkText;
                  const q = useLinked ? linkText : codeFindQuery;
                  const matches = useLinked ? linkMatches : codeMatches;
                  const cur = useLinked ? 0 : codeFindIdx;
                  return (
                    <div
                      className={`code-find-overlay${useLinked ? " linked" : ""}`}
                      aria-hidden="true"
                      ref={codeFindOverlayRef}
                      dangerouslySetInnerHTML={{
                        __html: buildCodeFindHtml(code, matches, q.length, cur),
                      }}
                    />
                  );
                })()
              ) : null}
              <CodeEditor
                ref={editorRef}
                value={code}
                language={language}
                placeholder={texts.app.codePlaceholder}
                onChange={(evn) => handleCodeChange(evn.target.value)}
                onCompositionStart={handleEditorCompositionStart}
                onCompositionEnd={handleEditorCompositionEnd}
                onKeyDown={handleEditorKeyDown}
                padding={15}
                data-color-mode={darkMode ? "dark" : "light"}
                className="code-editor"
                style={{
                  fontSize: 14,
                  backgroundColor: darkMode ? "#1e1e1e" : "#f8fafc",
                  fontFamily: '"Fira Mono", "Monaco", "Cascadia Code", "Segoe UI Mono", monospace',
                }}
              />
            </div>
          </div>}

          {showEditor && (
            <div
              className="split-divider"
              role="separator"
              aria-orientation={layout === 'horizontal' ? 'vertical' : 'horizontal'}
              aria-valuemin={MIN_SPLIT_RATIO}
              aria-valuemax={MAX_SPLIT_RATIO}
              aria-valuenow={Math.round(splitRatio)}
              aria-label="Resize editor and preview"
              tabIndex={0}
              onMouseDown={startSplitDrag}
              onTouchStart={startSplitDrag}
              onKeyDown={(e) => {
                const step = e.shiftKey ? 5 : 2;
                if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
                  e.preventDefault();
                  setSplitRatio((r) => {
                    const next = Math.max(MIN_SPLIT_RATIO, r - step);
                    try { window.localStorage.setItem(SPLIT_RATIO_KEY, String(next)); } catch (_) {}
                    return next;
                  });
                } else if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
                  e.preventDefault();
                  setSplitRatio((r) => {
                    const next = Math.min(MAX_SPLIT_RATIO, r + step);
                    try { window.localStorage.setItem(SPLIT_RATIO_KEY, String(next)); } catch (_) {}
                    return next;
                  });
                }
              }}
            />
          )}

          <div className="preview-pane">
            <div className="preview-header">
              <h2>{texts.app.preview}</h2>
              <div className="preview-controls">
                <div className="device-presets" title={texts.app.devicePreset || 'Device'}>
                  <select
                    className="device-select"
                    value={previewDevice}
                    onChange={handleDeviceChange}
                    aria-label={texts.app.devicePreset || 'Device'}
                  >
                    {DEVICE_PRESETS.map((preset) => (
                      <option key={preset.id} value={preset.id}>
                        {preset.id === 'responsive'
                          ? (texts.app.deviceCustom || 'Responsive')
                          : preset.label}
                      </option>
                    ))}
                  </select>
                  <span
                    className="preview-size-label"
                    title={texts.app.deviceSizeTitle || 'Preview size'}
                  >
                    <input
                      type="number"
                      className="size-input"
                      min={200}
                      max={4000}
                      step={1}
                      value={Math.round(previewSize.width)}
                      onChange={(e) => {
                        const w = Number(e.target.value);
                        if (!Number.isNaN(w) && e.target.value !== '') {
                          pinPreviewSize(w, previewSize.height);
                        }
                      }}
                      onBlur={(e) => {
                        const w = Number(e.target.value);
                        pinPreviewSize(
                          Number.isNaN(w) ? previewSize.width : w,
                          previewSize.height
                        );
                      }}
                      aria-label="Width"
                    /> × <input
                      type="number"
                      className="size-input"
                      min={120}
                      max={4000}
                      step={1}
                      value={Math.round(previewSize.height)}
                      onChange={(e) => {
                        const h = Number(e.target.value);
                        if (!Number.isNaN(h) && e.target.value !== '') {
                          pinPreviewSize(previewSize.width, h);
                        }
                      }}
                      onBlur={(e) => {
                        const h = Number(e.target.value);
                        pinPreviewSize(
                          previewSize.width,
                          Number.isNaN(h) ? previewSize.height : h
                        );
                      }}
                      aria-label="Height"
                    />
                  </span>
                </div>
                <div className="paper-presets">
                  <button
                    type="button"
                    className="preview-icon-btn"
                    onClick={handlePrint}
                    disabled={!code?.trim()}
                    title={texts.app.printTitle || 'Print'}
                    aria-label={texts.app.print || 'Print'}
                  >
                    <FiPrinter />
                  </button>
                  {printIssues.length > 0 && (
                    <div className="print-caution-wrap">
                      <button
                        type="button"
                        className="preview-icon-btn print-caution-btn"
                        onClick={() => setShowPrintCaution((v) => !v)}
                        title={texts.app.printCaution?.title || '印刷時に余白が消えるおそれがあります'}
                        aria-label={texts.app.printCaution?.title || '印刷の注意'}
                      >
                        <FiAlertTriangle />
                      </button>
                      {showPrintCaution && (
                        <div className="print-caution-popover" role="dialog">
                          <div className="print-caution-title">
                            {texts.app.printCaution?.title || '印刷時に余白が消えるおそれがあります'}
                          </div>
                          <ul className="print-caution-list">
                            {printIssues.map((iss, i) => (
                              <li key={i}>
                                {iss.kind === 'overflow'
                                  ? (texts.app.printCaution?.itemOverflow || '{selector} の印刷時に内容が消えるおそれがあります（スクロール/固定配置）').replace('{selector}', iss.selector)
                                  : iss.kind === 'padding'
                                    ? (texts.app.printCaution?.itemPadding || '{selector} の余白（padding）が印刷時に消えます').replace('{selector}', iss.selector)
                                    : (texts.app.printCaution?.itemMargin || '{selector} の余白（margin）が印刷時に消えます').replace('{selector}', iss.selector)}
                              </li>
                            ))}
                          </ul>
                          <div className="print-caution-actions">
                            <button
                              type="button"
                              className="btn-primary"
                              onClick={handleFixPrint}
                            >
                              {texts.app.printCaution?.fix || '修正する'}
                            </button>
                            <button
                              type="button"
                              className="btn-ghost"
                              onClick={() => setShowPrintCaution(false)}
                            >
                              {texts.app.printCaution?.dismiss || '閉じる'}
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
                <button
                  type="button"
                  className={`console-toggle ${showConsole ? 'active' : ''}`}
                  onClick={() => setShowConsole(!showConsole)}
                  title={texts.app.console}
                  aria-label={texts.app.console}
                >
                  <FiTerminal />
                  <span className="btn-label">
                    {texts.app.console}
                    {(consoleOutput.length + errors.length > 0) && ` (${consoleOutput.length + errors.length})`}
                  </span>
                  {(consoleOutput.length + errors.length > 0) && (
                    <span className="console-toggle-badge">{consoleOutput.length + errors.length}</span>
                  )}
                </button>
                <button
                  type="button"
                  className={`console-toggle network-toggle ${showConsole && activeTab === 'network' ? 'active' : ''}`}
                  onClick={() => { setShowConsole(true); setActiveTab('network'); }}
                  title={(texts.network && texts.network.networkTab) || 'Network'}
                  aria-label={(texts.network && texts.network.networkTab) || 'Network'}
                >
                  <FiActivity />
                  <span className="btn-label">
                    {(texts.network && texts.network.networkTab) || 'Network'}
                    {(networkLogs.length > 0) && ` (${networkLogs.length})`}
                  </span>
                  {(networkLogs.length > 0) && (
                    <span className="console-toggle-badge">{networkLogs.length}</span>
                  )}
                </button>
                <button
                  type="button"
                  className="console-toggle settings-toggle"
                  onClick={() => setShowWhitelist(true)}
                  title={(texts.network && texts.network.whitelistTitle) || 'Whitelist'}
                  aria-label={(texts.network && texts.network.whitelistTitle) || 'Whitelist'}
                >
                  <FiSettings />
                </button>
                {currentTemplate === "html" && (
                  <button
                    type="button"
                    className={`preview-icon-btn link-toggle ${linkMode ? 'active' : ''}`}
                    onClick={toggleLinkMode}
                    title={texts?.app?.linkTitle}
                    aria-label={texts?.app?.linkTitle}
                  >
                    <FiLink />
                  </button>
                )}
                <button
                  type="button"
                  className={`preview-icon-btn find-toggle ${previewFindOpen ? 'active' : ''}`}
                  onClick={() => setPreviewFindOpen((v) => !v)}
                  title={texts.app.findPreviewTitle}
                  aria-label={texts.app.find}
                >
                  <FiSearch />
                </button>
                {linkInfo && <span className="link-info">{linkInfo}</span>}
              </div>
            </div>

            <AiEditPanel
              code={code}
              template={currentTemplate}
              onApply={handleAiApply}
              texts={texts}
              placement="preview"
              paperSkill={paperSkill}
              onPaperSkillChange={setPaperSkill}
            />

            <div className="preview-stage">
            {(() => {
              const previewBody = (!code && !srcDoc) ? (
                <div className="empty-preview">
                  <div className="empty-preview-content">
                    <h3>{texts.app.emptyPreview?.title || '実行結果がここに表示されます'}</h3>
                    <p>{texts.app.emptyPreview?.description || 'コードエディタにコードを書いて「実行」を押すと、ここにプレビューが表示されます'}</p>
                  </div>
                </div>
              ) : (
                <div ref={previewScrollRef} className="preview-scroll-host">
                  {previewFindOpen && (
                    <FindBar
                      className="find-bar--preview"
                      texts={texts}
                      query={previewFindQuery}
                      onQueryChange={onPreviewFindQueryChange}
                      onNext={previewFindNext}
                      onPrev={previewFindPrev}
                      matchCount={previewFindCount}
                      currentIndex={previewFindCurrent}
                      onClose={() => setPreviewFindOpen(false)}
                      placeholder={texts.app.findPlaceholderPreview}
                    />
                  )}
                  <iframe
                    key={previewKey}
                    ref={iframeRef}
                    title="preview"
                    sandbox="allow-scripts"
                    srcDoc={srcDoc}
                    onLoad={handleIframeLoad}
                    className="preview-iframe"
                  />
                </div>
              );

              if (previewFit || fullscreen) {
                return (
                  <div
                    ref={previewFrameRef}
                    className="preview-frame preview-frame--fill"
                  >
                    {previewBody}
                  </div>
                );
              }

              return (
                <div
                  className="preview-frame"
                  style={{
                    width: previewSize.width,
                    height: previewSize.height,
                  }}
                >
                  {previewBody}
                </div>
              );
            })()}

            {showConsole && (
              <div className="console-panel">
                <div className="console-header">
                  <div className="panel-tabs">
                    <button
                      className={`panel-tab ${activeTab === 'console' ? 'active' : ''}`}
                      onClick={() => setActiveTab('console')}
                    >
                      <FiTerminal /> {(texts.network && texts.network.consoleTab) || texts.app.console}
                    </button>
                    <button
                      className={`panel-tab ${activeTab === 'network' ? 'active' : ''}`}
                      onClick={() => setActiveTab('network')}
                    >
                      <FiActivity /> {(texts.network && texts.network.networkTab) || 'Network'} {networkLogs.length > 0 && `(${networkLogs.length})`}
                    </button>
                    <button
                      className={`panel-tab ${activeTab === 'errors' ? 'active' : ''}`}
                      onClick={() => setActiveTab('errors')}
                    >
                      <FiX /> {(texts.network && texts.network.errorsTab) || 'Errors'} {errors.length > 0 && `(${errors.length})`}
                    </button>
                  </div>
                  <button
                    className="panel-clear-btn"
                    onClick={() => { setConsoleOutput([]); setErrors([]); setNetworkLogs([]); }}
                  >
                    {texts.app.clear}
                  </button>
                </div>
                <div className="console-content">
                  {activeTab === 'network' ? (
                    <NetworkLogPanel logs={networkLogs} texts={texts} />
                  ) : activeTab === 'errors' ? (
                    errors.length === 0 ? (
                      <div className="console-empty">{(texts.network && texts.network.errorsEmpty) || 'エラーはありません'}</div>
                    ) : errors.map((error, index) => (
                      <div key={`error-${index}`} className="console-item error">
                        <span className="timestamp">{error.timestamp}</span>
                        <span className="method">ERROR</span>
                        <span className="message">
                          {error.message} (Line: {error.line}, Column: {error.column})
                        </span>
                      </div>
                    ))
                  ) : (
                    consoleOutput.length === 0 ? (
                      <div className="console-empty">{(texts.network && texts.network.consoleEmpty) || 'コンソール出力はありません'}</div>
                    ) : consoleOutput.map((output, index) => (
                      <div key={`console-${index}`} className={`console-item ${output.method}`}>
                        <span className="timestamp">{output.timestamp}</span>
                        <span className="method">{output.method.toUpperCase()}</span>
                        <span className="message">{output.args.join(' ')}</span>
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}
            </div>

            {pendingConfirm && (
              <NetworkConfirmModal
                request={pendingConfirm}
                texts={texts}
                onDecide={handleNetworkConfirm}
              />
            )}

            {showWhitelist && (
              <WhitelistSettings
                whitelist={whitelist}
                texts={texts}
                onChange={handleWhitelistChange}
                onClose={() => setShowWhitelist(false)}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default App;
